import React, { useState, useEffect } from 'react';
import { apiUrl } from '../../utils/apiBase';
import { Mail, CheckCircle, Plus, Edit2, Trash2, Save, X, ShieldAlert } from 'lucide-react';

interface IntegracaoFluxosProps {
  session: any;
}

interface ConfiguradorEmail {
  id?: string;
  _id?: string;
  nome: string;
  emailDestino: string;
  descricao?: string;
  ativo: boolean;
  criadoEm: string;
  criadoPor: string;
}

const IntegracaoFluxosSeguro: React.FC<IntegracaoFluxosProps> = ({ session }) => {
  const [configuradores, setConfiguradores] = useState<ConfiguradorEmail[]>([]);
  const [mostrandoFormulario, setMostrandoFormulario] = useState(false);
  const [editandoConfig, setEditandoConfig] = useState<ConfiguradorEmail | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [testandoEmail, setTestandoEmail] = useState<string | null>(null);
  
  // Verificar se usuário é administrador
  const isAdmin = ['administrator', 'admin'].includes(session?.usuario?.role);
  
  // Formulário
  const [formData, setFormData] = useState({
    nome: '',
    emailDestino: '',
    descricao: ''
  });

  // Carregar configuradores do MongoDB
  useEffect(() => {
    if (session?.empresa?.id || session?.usuario?.empresaId) {
      carregarConfiguradoresDoServidor();
    }
  }, [session?.empresa?.id, session?.usuario?.empresaId]);

  // Sincronizar com integrações sempre que configuradores mudarem
  useEffect(() => {
    if (configuradores.length > 0) {
      sincronizarComIntegracoes();
    }
  }, [configuradores]);

  const carregarConfiguradoresDoServidor = async () => {
    try {
      const empresaId = session?.empresa?.id || session?.usuario?.empresaId;
      const url = empresaId
        ? apiUrl(`/configuradores-email?empresaId=${empresaId}`)
        : apiUrl('/configuradores-email');
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.status === 'success') {
        setConfiguradores(data.configuradores || []);
      } else {
        console.error('Erro ao carregar configuradores:', data.message);
      }
    } catch (error) {
      console.error('Erro ao conectar com servidor MongoDB Atlas:', error);
    }
  };

  // Salvar configuradores no MongoDB e sincronizar com integrações
  const salvarConfiguradorNoServidor = async (config: ConfiguradorEmail, isEdit: boolean = false) => {
    try {
  const url = apiUrl('/configuradores-email');
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        // Recarregar lista do servidor
        await carregarConfiguradoresDoServidor();
        
        // 🎯 NOVO: Sincronizar com sistema de integrações
        await sincronizarComIntegracoes();
        
        return true;
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Erro ao salvar no servidor:', error);
      throw error; // Não usar localStorage, só MongoDB Atlas
    }
  };

  // Sincronizar configuradores com sistema de integrações
  const sincronizarComIntegracoes = async () => {
    try {
      console.log('🔄 Sincronizando configuradores com integrações...');
      
      // Converter configuradores para formato de integração
      const integracoesEmail = configuradores.map(config => ({
        id: config._id || config.id,
        nome: config.nome,
        tipo: 'email',
        descricao: config.descricao || `Envio para ${config.emailDestino}`,
        configuracao: {
          emailDestino: config.emailDestino,
          nomeConfigurador: config.nome,
          tipoIntegracao: 'configurador-email'
        },
        ativo: config.ativo,
        empresa: session?.empresa?.nome || 'Sistema Escalone'
      }));

      // Salvar no sistema de integrações
  const response = await fetch(apiUrl('/integracoes/sincronizar-email'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          integracoes: integracoesEmail,
          empresa: session?.empresa?.nome || 'Sistema Escalone'
        })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        console.log('✅ Configuradores sincronizados com integrações!');
      } else {
        console.error('❌ Erro ao sincronizar:', result.message);
      }
      
    } catch (error) {
      console.error('❌ Erro na sincronização:', error);
    }
  };

  const excluirConfiguradorDoServidor = async (id: string) => {
    try {
  const response = await fetch(apiUrl('/configuradores-email'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id })
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        // Recarregar lista do servidor
        await carregarConfiguradoresDoServidor();
        
        // Sincronizar com integrações após exclusão
        await sincronizarComIntegracoes();
        
        return true;
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Erro ao excluir do servidor MongoDB Atlas:', error);
      throw error; // Não usar localStorage, só MongoDB Atlas
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSalvando(true);

    try {
      const configData: ConfiguradorEmail = {
        id: editandoConfig?.id || editandoConfig?._id || Date.now().toString(),
        nome: formData.nome,
        emailDestino: formData.emailDestino,
        descricao: formData.descricao,
        ativo: true,
        criadoEm: editandoConfig?.criadoEm || new Date().toISOString(),
        criadoPor: session?.usuario?.email || ''
      };

      // Se editando, incluir o _id do MongoDB
      if (editandoConfig?._id) {
        (configData as any)._id = editandoConfig._id;
      }

      const sucesso = await salvarConfiguradorNoServidor(configData, !!editandoConfig);
      
      if (sucesso) {
        // Reset form
        setFormData({ nome: '', emailDestino: '', descricao: '' });
        setMostrandoFormulario(false);
        setEditandoConfig(null);
      } else {
        alert('Erro ao salvar configurador!');
      }
      
    } catch (error) {
      console.error('Erro ao salvar configurador:', error);
      alert('Erro ao salvar configurador!');
    } finally {
      setSalvando(false);
    }
  };

  const handleEditar = (config: ConfiguradorEmail) => {
    if (!isAdmin) {
      alert('⚠️ Apenas administradores podem editar configuradores de email.');
      return;
    }
    setEditandoConfig(config);
    setFormData({
      nome: config.nome,
      emailDestino: config.emailDestino,
      descricao: config.descricao || ''
    });
    setMostrandoFormulario(true);
  };

  const handleExcluir = async (id: string) => {
    if (!isAdmin) {
      alert('⚠️ Apenas administradores podem excluir configuradores de email.');
      return;
    }
    if (confirm('Tem certeza que deseja excluir este configurador?')) {
      await excluirConfiguradorDoServidor(id);
    }
  };

  const handleTestarEmail = async (config: ConfiguradorEmail) => {
    if (!isAdmin) {
      alert('⚠️ Apenas administradores podem testar o envio de emails.');
      return;
    }
    const configId = config._id || config.id || '';
    setTestandoEmail(configId);
    
    try {
  const response = await fetch(apiUrl('/upload-arquivo'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file: {
            name: 'teste-configurador.txt',
            size: 100,
            type: 'text/plain',
            dataUrl: 'data:text/plain;base64,VGVzdGUgZG8gY29uZmlndXJhZG9yIGRlIGVtYWls'
          },
          usuario: {
            nome: session?.usuario?.nome || 'Usuário Sistema',
            email: session?.usuario?.email || 'usuario@sistema.com',
            empresa: session?.empresa?.nome || 'Sistema Escalone'
          },
          emailDestino: config.emailDestino,
          nomeConfigurador: config.nome
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        alert(`✅ Email de teste enviado para ${config.emailDestino}!`);
      } else {
        throw new Error(result.message || 'Erro no teste');
      }
    } catch (error) {
      console.error('Erro no teste:', error);
      alert('❌ Erro ao enviar email de teste!');
    } finally {
      setTestandoEmail(null);
    }
  };

  const cancelarFormulario = () => {
    setMostrandoFormulario(false);
    setEditandoConfig(null);
    setFormData({ nome: '', emailDestino: '', descricao: '' });
  };

  return (
  <div className="flex-1 min-h-screen bg-gradient-to-br from-white via-white to-purple-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="relative bg-white rounded-2xl shadow-lg border border-gray-200 mb-6 overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-600 to-purple-600" />
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-extrabold text-gray-900 flex items-center gap-2 tracking-tight">
                  <Mail className="h-6 w-6 text-blue-600" />
                  Configurador de Emails para Fluxos
                </h1>
                <p className="text-gray-600 mt-1">
                  Crie e gerencie destinos de email para usar nas automações de anexos
                </p>
                {!isAdmin && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-orange-600 bg-orange-50 px-3 py-2 rounded-lg">
                    <ShieldAlert className="h-4 w-4" />
                    <span className="font-medium">Visualização apenas - Apenas administradores podem editar</span>
                  </div>
                )}
              </div>
              {isAdmin && (
                <button
                  onClick={() => setMostrandoFormulario(true)}
                  className="px-4 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Novo Configurador</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Formulário */}
        {mostrandoFormulario && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editandoConfig ? '✏️ Editar Configurador' : '➕ Novo Configurador'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Configurador *
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    placeholder="Ex: Relatórios Gerenciais"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email de Destino *
                  </label>
                  <input
                    type="email"
                    value={formData.emailDestino}
                    onChange={(e) => setFormData({...formData, emailDestino: e.target.value})}
                    placeholder="Ex: relatorios@refricril.com.br"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição (opcional)
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                  placeholder="Ex: Para envio de relatórios mensais da gerência"
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={salvando}
                  className="px-4 py-2 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                >
                  {salvando ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Salvar</span>
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={cancelarFormulario}
                  className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:border-gray-400 bg-white transition-colors flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  <span>Cancelar</span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lista de Configuradores */}
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            📋 Configuradores Criados ({configuradores.length})
          </h2>
          
          {configuradores.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-lg">Nenhum configurador criado ainda</p>
              <p className="text-sm">Clique em "Novo Configurador" para começar</p>
            </div>
          ) : (
      <div className="space-y-3">
              {configuradores.map((config) => (
        <div key={config._id || config.id} className="border border-gray-200 rounded-xl p-4 bg-gradient-to-br from-gray-50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-medium text-gray-900">{config.nome}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          config.ativo ? 'bg-indigo-100 text-indigo-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {config.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        📧 <strong>{config.emailDestino}</strong>
                      </div>
                      {config.descricao && (
                        <div className="mt-1 text-sm text-gray-500">
                          {config.descricao}
                        </div>
                      )}
                      <div className="mt-2 text-xs text-gray-400">
                        Criado em: {new Date(config.criadoEm).toLocaleString('pt-BR')} por {config.criadoPor}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => handleTestarEmail(config)}
                            disabled={testandoEmail === (config._id || config.id)}
                            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 shadow"
                          >
                            {testandoEmail === (config._id || config.id) ? '⏳' : '📧 Testar'}
                          </button>
                          
                          <button
                            onClick={() => handleEditar(config)}
                            className="text-blue-700 hover:text-blue-800 p-1.5 rounded-lg hover:bg-blue-50"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          
                          <button
                            onClick={() => handleExcluir(config._id || config.id!)}
                            className="text-red-600 hover:text-red-800 p-1.5 rounded-lg hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {!isAdmin && (
                        <span className="text-xs text-gray-400 italic">Apenas visualização</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Como Usar nos Fluxos */}
  <div className="mt-6 bg-indigo-50/70 border border-indigo-200 rounded-xl p-4">
          <div className="flex items-center space-x-2 mb-3">
            <CheckCircle className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-blue-900">Como usar nos Fluxos</span>
          </div>
          <div className="text-sm text-blue-800 space-y-1">
            <div>✅ <strong>1.</strong> Crie configuradores aqui com nomes descritivos</div>
            <div>✅ <strong>2.</strong> No fluxo, escolha qual configurador usar para anexos</div>
            <div>✅ <strong>3.</strong> O sistema enviará automaticamente para o email configurado</div>
            <div>✅ <strong>4.</strong> Use o botão "Testar" para verificar se o email funciona</div>
          </div>
        </div>

        {/* Status */}
        <div className="mt-6 bg-indigo-50/70 border border-indigo-200 rounded-xl p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-indigo-600" />
            <span className="font-medium text-indigo-900">Sistema Conectado</span>
          </div>
          <div className="mt-2 text-sm text-indigo-800">
  🔧 Backend: <strong>/api</strong> (auto) |
      • SMTP: <strong>smtp.emailzimbraonline.com</strong> | 
            ✅ <strong>Funcionando!</strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegracaoFluxosSeguro;